import React, { useMemo, useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { jsPDF } from 'jspdf';
import { Search, MapPin, Calendar, Clock, DollarSign, Building2, AlertCircle, Loader2, Eye, EyeOff, BookmarkCheck, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Globe, Paperclip, Hash, Grid, FileText, Tag, Trash2, RefreshCw, Save, CalendarDays, RotateCcw, Download, Ban, Bot, CheckCircle2, Archive, ArrowLeftRight, Repeat2, ShoppingCart, X } from 'lucide-react';

import { ExternalTender, Tender } from '../types';
import { theme } from '../config/theme';
import { useModal } from './ModalProvider';
import { apiService } from '../services/apiService';

import ClearFiltersButton from './ClearFiltersButton';
import { getAuthHeaders } from '../config/api';

const fetch = (input: RequestInfo | URL, init: RequestInit = {}) =>
  globalThis.fetch(input, {
    ...init,
    headers: { ...getAuthHeaders(), ...(init.headers || {}) },
  });

const OPPORTUNITIES_PER_PAGE = 50;
type OpportunityView = 'ACTIVE' | 'FAVORITED' | 'ARCHIVED' | 'DISCARDED';
type ReadFilter = 'ALL' | 'READ' | 'UNREAD';
type DeadlineSort = 'DEADLINE_ASC' | 'QUALITY_DESC' | 'NEWEST';
type ExportFormat = 'CSV' | 'XML' | 'PDF';
type OpportunityContractType = 'LEASE' | 'ACQUISITION' | 'BOTH' | 'UNKNOWN';
type BatchAction = 'FAVORITE' | 'ARCHIVE' | 'PARTICIPATE' | 'DO_NOT_PARTICIPATE' | 'RESTORE';

const BATCH_ACTIONS: Record<OpportunityView, Array<{
  action: BatchAction;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  className: string;
}>> = {
  ACTIVE: [
    { action: 'FAVORITE', label: 'Adicionar à participação', icon: BookmarkCheck, className: 'border-brand-500 bg-brand-600 text-white hover:bg-brand-700' },
    { action: 'ARCHIVE', label: 'Descartar', icon: Trash2, className: 'border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-950/30' },
  ],
  FAVORITED: [
    { action: 'PARTICIPATE', label: 'Participar', icon: CheckCircle2, className: 'border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300' },
    { action: 'DO_NOT_PARTICIPATE', label: 'Não participar', icon: Ban, className: 'border-zinc-400 text-zinc-700 hover:bg-zinc-100 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800' },
    { action: 'ARCHIVE', label: 'Descartar', icon: Trash2, className: 'border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-950/30' },
  ],
  ARCHIVED: [
    { action: 'ARCHIVE', label: 'Descartar', icon: Trash2, className: 'border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-950/30' },
  ],
  DISCARDED: [
    { action: 'RESTORE', label: 'Restaurar', icon: RotateCcw, className: 'border-brand-500 bg-brand-600 text-white hover:bg-brand-700' },
  ],
};

const OPPORTUNITY_CATEGORY_LABELS: Record<string, string> = {
  CONTROLE_PONTO: 'Controle de Ponto',
  CONTROLE_ACESSO: 'Controle de Acesso',
  SEGURANCA_CFTV: 'Segurança, CFTV e Alarmes',
};

const CONTRACT_TYPE_META = {
  LEASE: {
    label: 'Locação',
    icon: Repeat2,
    className: 'border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-800 dark:bg-cyan-950/30 dark:text-cyan-300',
  },
  ACQUISITION: {
    label: 'Aquisição',
    icon: ShoppingCart,
    className: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300',
  },
  BOTH: {
    label: 'Ambos',
    icon: ArrowLeftRight,
    className: 'border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300',
  },
  UNKNOWN: {
    label: 'Não identificado',
    icon: AlertCircle,
    className: 'border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark',
  },
} as const;

const normalizeFilterText = (value: unknown) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLocaleLowerCase('pt-BR');

const getOpportunityCategory = (tender: ExternalTender) => {
  const category = String(tender.matchDetails?.category || '').trim();
  const linkedCategory = (tender.terms || []).find((term) => OPPORTUNITY_CATEGORY_LABELS[String(term.term || '').trim()]);
  return String(
    tender.matchDetails?.profile
    || OPPORTUNITY_CATEGORY_LABELS[category]
    || category
    || linkedCategory?.label
    || OPPORTUNITY_CATEGORY_LABELS[String(linkedCategory?.term || '').trim()]
    || 'Não classificada',
  );
};

const classifyOpportunityContract = (tender: ExternalTender): OpportunityContractType => {
  const searchableText = normalizeFilterText([
    tender.objeto,
    ...(tender.itens || []).map((item) => item.descricao),
    ...(tender.matchDetails?.commercialTerms || []),
    ...(tender.matchDetails?.businessModels || []),
  ].join(' '));

  const hasLease = [
    'locacao',
    'aluguel',
    'comodato',
    'licenca mensal',
    'licenca anual',
    'licenca locada',
    'licenca recorrente',
    'subscricao',
    'assinatura de software',
    'software como servico',
    'saas',
  ].some((term) => searchableText.includes(term));

  const hasAcquisition = [
    'aquisicao',
    'compra de equipamento',
    'compra de software',
    'fornecimento de equipamento',
    'fornecimento de hardware',
    'licenca perpetua',
    'licenca vitalicia',
    'licenca definitiva',
    'direito de uso permanente',
  ].some((term) => searchableText.includes(term));

  if (hasLease && hasAcquisition) return 'BOTH';
  if (hasLease) return 'LEASE';
  if (hasAcquisition) return 'ACQUISITION';
  return 'UNKNOWN';
};

const parseExternalDeadline = (raw?: string): Date | null => {
  if (!raw) return null;
  const normalized = raw.trim();
  const brazilianDate = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (brazilianDate) {
    const [, dayRaw, monthRaw, yearRaw, hourRaw = '23', minuteRaw = '59', secondRaw = '0'] = brazilianDate;
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    const second = Number(secondRaw);
    const parsed = new Date(year, month - 1, day, hour, minute, second);
    const isExactDate = parsed.getFullYear() === year
      && parsed.getMonth() === month - 1
      && parsed.getDate() === day
      && parsed.getHours() === hour
      && parsed.getMinutes() === minute;
    return isExactDate ? parsed : null;
  }
  const timestamp = Date.parse(normalized);
  return Number.isNaN(timestamp) ? null : new Date(timestamp);
};

const dateInputTimestamp = (value: string, endOfDay = false) => {
  if (!value) return null;
  const parsed = new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const getExternalDaysRemaining = (encerramento?: string) => {
  const endDate = parseExternalDeadline(encerramento);
  if (!endDate) return null;
  const endDay = new Date(endDate);
  endDay.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((endDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

interface ExternalTenderListProps {
  externalSearchTerm?: string;
  externalSetSearchTerm?: (s: string) => void;
  externalStateFilter?: string;
  externalSetStateFilter?: (s: string) => void;
  externalClearFilters?: () => void;
  onAddToWallet?: (t: ExternalTender) => Promise<Tender | void>;
  initialView?: OpportunityView;
}

const ExternalTenderList: React.FC<ExternalTenderListProps> = ({
  externalSearchTerm,
  externalSetSearchTerm,
  externalStateFilter,
  externalSetStateFilter,
  externalClearFilters,
  onAddToWallet,
  initialView = 'ACTIVE',
}) => {
  const [tenders, setTenders] = useState<ExternalTender[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [searchRuns, setSearchRuns] = useState<any[]>([]);
  const [runningPncp, setRunningPncp] = useState(false);
  const [pncpSummary, setPncpSummary] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // local state used when no external controller is provided
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [localStateFilter, setLocalStateFilter] = useState('ALL');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [modalityFilter, setModalityFilter] = useState('ALL');
  const [qualityFilter, setQualityFilter] = useState('ALL');
  const [opportunityView, setOpportunityView] = useState<OpportunityView>(initialView);
  const [readFilter, setReadFilter] = useState<ReadFilter>('ALL');
  const [deadlineWindow, setDeadlineWindow] = useState('ALL');
  const [deadlineFrom, setDeadlineFrom] = useState('');
  const [deadlineTo, setDeadlineTo] = useState('');
  const [deadlineSort, setDeadlineSort] = useState<DeadlineSort>('DEADLINE_ASC');
  const [viewCounts, setViewCounts] = useState({ active: 0, read: 0, unread: 0, favorited: 0, archived: 0, discarded: 0 });
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [discardingId, setDiscardingId] = useState<string | null>(null);
  const [trackingResultId, setTrackingResultId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [autoDiscardedCount, setAutoDiscardedCount] = useState(0);
  const [expandedTender, setExpandedTender] = useState<string | null>(null);
  const [favoritingId, setFavoritingId] = useState<string | null>(null);
  const [selectedTenderIds, setSelectedTenderIds] = useState<Set<string>>(new Set());
  const [batchAction, setBatchAction] = useState<BatchAction | null>(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const pollingPncpRef = useRef(false);
  const mountedRef = useRef(true);

  const { showConfirm, showAlert } = useModal();

  const favoriteTender = async (tender: ExternalTender) => {
    if (!onAddToWallet || tender.favoriteTenderId || favoritingId) return;
    try {
      setFavoritingId(tender.id);
      const created = await onAddToWallet(tender);
      if (created) {
        setTenders((current) => current.filter((item) => item.id !== tender.id));
        void loadViewCounts();
      }
    } finally {
      setFavoritingId(null);
    }
  };

  const toggleResultTracking = async (tender: ExternalTender) => {
    if (!tender.favoriteTenderId || trackingResultId) return;
    try {
      setTrackingResultId(tender.id);
      const updated = await apiService.setTenderResultTracking(tender.favoriteTenderId, !tender.favoriteTrackResult);
      setTenders((current) => current.map((item) => item.id === tender.id
        ? { ...item, favoriteTrackResult: Boolean(updated.trackResult) }
        : item));
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao atualizar o acompanhamento.', 'Erro');
    } finally {
      setTrackingResultId(null);
    }
  };

  const BRAZIL_STATES = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

  useEffect(() => {
    mountedRef.current = true;
    loadProfiles();
    loadSearchRuns();
    resumePncpSearch();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    void loadExternalTenders(opportunityView);
    void loadViewCounts();
  }, [opportunityView]);

  useEffect(() => {
    setOpportunityView(initialView);
  }, [initialView]);

  const loadProfiles = async () => {
    try {
      const response = await fetch('/api/pncp/search-profiles');
      if (!response.ok) throw new Error(`Erro ${response.status}`);
      setProfiles(await response.json());
    } catch (error) {
      console.error('Erro ao carregar perfis PNCP:', error);
    }
  };

  const loadSearchRuns = async () => {
    try {
      const response = await fetch('/api/pncp/search-runs?limit=30');
      if (!response.ok) throw new Error(`Erro ${response.status}`);
      setSearchRuns(await response.json());
    } catch (error) {
      console.error('Erro ao carregar histórico PNCP:', error);
    }
  };

  const clearFilters = () => {
    setSourceFilter('ALL');
    setCategoryFilter('ALL');
    setModalityFilter('ALL');
    setQualityFilter('ALL');
    setReadFilter('ALL');
    setDeadlineWindow('ALL');
    setDeadlineFrom('');
    setDeadlineTo('');
    setDeadlineSort('DEADLINE_ASC');
    setCurrentPage(1);
    if (externalClearFilters) return externalClearFilters();
    setLocalSearchTerm('');
    setLocalStateFilter('ALL');
  };

  const loadViewCounts = async () => {
    try {
      const response = await fetch('/api/tenders/external/counts');
      if (!response.ok) throw new Error(`Erro ${response.status}`);
      setViewCounts(await response.json());
    } catch (error) {
      console.error('Erro ao carregar contadores de oportunidades:', error);
    }
  };

  const loadExternalTenders = async (view: OpportunityView = opportunityView) => {
    try {
      setIsLoading(true);

      const response = await fetch(`/api/tenders/external?view=${encodeURIComponent(view)}`);

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Erro ao carregar licitações: ${response.status} ${text}`);
      }

      const json = await response.json();
      setAutoDiscardedCount(Number(response.headers.get('X-Auto-Discarded') || 0));
      if (Array.isArray(json)) {
        setTenders(json as ExternalTender[]);
        return;
      }
      type EffectiEntry = Record<string, unknown>;
      const licitacoes: EffectiEntry[] = Array.isArray(json?.licitacoes) ? json.licitacoes : [];

      // Mapear para o tipo Local ExternalTender usado pelo frontend
      const parseBrazilNumber = (v: unknown): number | undefined => {
        if (v === null || v === undefined) return undefined;
        if (typeof v === 'number' && !Number.isNaN(v)) return v;
        if (typeof v !== 'string') return undefined;
        // Remove currency symbol and spaces
        let s = v.replace(/[^0-9,.-]/g, '').trim();
        if (s === '') return undefined;
        // If contains both '.' and ',', assume '.' is thousand separator and ',' is decimal
        if (s.indexOf('.') !== -1 && s.indexOf(',') !== -1) {
          s = s.replace(/\./g, '').replace(',', '.');
        } else if (s.indexOf(',') !== -1 && s.indexOf('.') === -1) {
          // '1234,56' -> '1234.56'
          s = s.replace(',', '.');
        } else {
          // '1234.56' remains
        }
        const n = Number(s);
        return Number.isFinite(n) ? n : undefined;
      };

      const mapped: ExternalTender[] = licitacoes.map((l) => {
        const entry = l as unknown as Record<string, unknown>;
        const getRaw = (k: string) => entry[k];
        const getString = (k: string) => {
          const v = getRaw(k);
          return typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : '');
        };
        const getNumber = (k: string) => {
          const v = getRaw(k);
          return typeof v === 'number' ? v : (typeof v === 'string' && v !== '' ? Number(v) : undefined);
        };

        // Alguns endpoints do Effecti usam nomes diferentes para a lista de itens.
        // Tentamos várias chaves comuns para aumentar a compatibilidade com payloads reais.
        const itensRaw = Array.isArray(entry['itensEdital'])
          ? (entry['itensEdital'] as unknown[])
          : Array.isArray(entry['itens'])
          ? (entry['itens'] as unknown[])
          : Array.isArray(entry['itens_edital'])
          ? (entry['itens_edital'] as unknown[])
          : Array.isArray(entry['itensEditais'])
          ? (entry['itensEditais'] as unknown[])
          : [];

        const endereco = entry['endereco'] as Record<string, unknown> | undefined;

        // Parse items first so we can fallback to summing them when tender-level valorTotal is missing
        const parsedItems = itensRaw.map((it) => {
          const item = it as Record<string, unknown>;
          const parseInteger = (v: unknown): number => {
            if (v === null || v === undefined) return 0;
            if (typeof v === 'number' && Number.isFinite(v)) return Math.round(v);
            if (typeof v === 'string') {
              const digits = v.replace(/[^0-9-]/g, '');
              const n = Number(digits);
              return Number.isFinite(n) ? Math.round(n) : 0;
            }
            return 0;
          };

          const quantidadeRaw = item['quantidade'] ?? item['qtd'] ?? item['quantidadeItem'] ?? item['quantidade_material'];

          const parseBoolean = (v: unknown): boolean => {
            if (v === null || v === undefined) return false;
            if (typeof v === 'boolean') return v;
            if (typeof v === 'number') return v !== 0;
            if (typeof v === 'string') {
              const s = v.trim().toLowerCase();
              return ['1', 'true', 'sim', 's', 'yes', 'y'].includes(s);
            }
            return false;
          };

          const parseMaybeString = (keys: string[]): string => {
            for (const k of keys) {
              const v = item[k];
              if (typeof v === 'string' && v.trim() !== '') return v;
              if (typeof v === 'number') return String(v);
            }
            return '';
          };

          const itemNumber = (() => {
            const cand = item['item'] ?? item['numero'] ?? item['nItem'] ?? item['numItem'];
            if (typeof cand === 'number') return Math.round(cand);
            if (typeof cand === 'string') {
              const digits = cand.replace(/[^0-9-]/g, '');
              const n = Number(digits);
              return Number.isFinite(n) ? Math.round(n) : undefined;
            }
            return undefined;
          })();

          const loteVal = item['lote'] ?? item['numeroLote'] ?? item['loteNumero'] ?? undefined;
          const diferenciadoRaw = item['diferenciado'] ?? item['exclusivo'] ?? item['exclusivoMeEpp'] ?? item['apenasMeEpp'] ?? undefined;

          const valorUnitario = (() => {
            const raw = item['valorUnitario'] ?? item['valor'] ?? item['preco'];
            return typeof raw === 'number' ? raw as number : (parseBrazilNumber(raw) ?? 0);
          })();

          const valorTotalItem = (() => {
            const raw = item['valorTotal'] ?? item['valor_estimado'] ?? item['valor'] ?? null;
            return typeof raw === 'number' ? raw as number : (parseBrazilNumber(raw) ?? 0);
          })();

          return {
            descricao: (typeof item['produtoLicitado'] === 'string'
              ? item['produtoLicitado']
              : typeof item['descricao'] === 'string'
              ? item['descricao']
              : '') as string,
            quantidade: parseInteger(quantidadeRaw),
            unidade: typeof item['unidade'] === 'string' ? item['unidade'] as string : (typeof item['unidadeMedida'] === 'string' ? item['unidadeMedida'] as string : ''),
            valorUnitario,
            valorTotal: valorTotalItem,
            item: itemNumber,
            lote: typeof loteVal === 'number' || typeof loteVal === 'string' ? loteVal : undefined,
            diferenciado: parseBoolean(diferenciadoRaw),
            grupo: parseMaybeString(['grupo', 'grupoNome', 'grupoDescricao', 'grupo_material', 'grupoMaterial']),
          };
        });

        const tenderDeclared = (() => {
          // Verifica várias chaves possíveis que podem conter o valor total no payload
          const a = parseBrazilNumber(getRaw('valorTotal')) ?? parseBrazilNumber(getRaw('valor_total'));
          const b = parseBrazilNumber(getRaw('valorEstimado')) ?? parseBrazilNumber(getRaw('valor_estimado')) ?? parseBrazilNumber(getRaw('valor_total_estimado'));
          return a ?? b ?? undefined;
        })();

        const sumFromItems = parsedItems.reduce((s, it) => s + (typeof it.valorTotal === 'number' ? it.valorTotal : 0), 0);

        // If tender-level value exists, prefer it; otherwise use sum of items when available (>0)
        const finalValorTotal = tenderDeclared ?? (sumFromItems > 0 ? sumFromItems : undefined);

        return {
          id: String(getRaw('idLicitacao') ?? getRaw('id') ?? ''),
          edital: getString('processo') || String(getRaw('idLicitacao') ?? ''),
          estado: getString('uf') || (endereco && typeof endereco.uf === 'string' ? endereco.uf : ''),
          cidade: getString('cidade') || '',
          orgao: getString('orgao') || getString('unidadeGestora') || '',
          objeto: getString('objeto') || getString('descricao') || '',
          modalidade: getString('modalidade') || getString('perfilNome') || '',
          recebimentoPropostas: getString('dataInicialProposta') || '',
          encerramento: getString('dataFinalProposta') || getString('dataFinal') || '',
          incluido: getString('dataPublicacao') || getString('dataCaptura') || '',
          atualizado: getRaw('dataAtualizacao') as string | undefined,
          portal: getString('portal') || undefined,
          uasg: getNumber('uasg') ?? undefined,
          perfilNome: getString('perfilNome') || undefined,
          itens: parsedItems,
          valorTotal: finalValorTotal,
          // parse items first so we can fallback to summing them when tender-level valorTotal is missing
          // we parse below and then include `itens` and `valorTotal` in the returned object
          // (computed before the returned object to keep the object literal clean)
          // Note: parsedItems will be assembled just prior to returning the full mapped object.
          // attachmentUrl: prefer first anexos[].url when present
          attachmentUrl: Array.isArray(getRaw('anexos')) && (getRaw('anexos') as unknown[])[0] && (((getRaw('anexos') as unknown[])[0] as Record<string, unknown>)['url'])
            ? (((getRaw('anexos') as unknown[])[0] as Record<string, unknown>)['url'] as string)
            : undefined,
          // portalUrl: some APIs provide a top-level `url` for the portal
          portalUrl: getString('url') || undefined,
          urlEdital: Array.isArray(getRaw('anexos')) && (getRaw('anexos') as unknown[])[0] && (((getRaw('anexos') as unknown[])[0] as Record<string, unknown>)['url'])
            ? (((getRaw('anexos') as unknown[])[0] as Record<string, unknown>)['url'] as string)
            : (getRaw('url') as string | undefined) ?? undefined,
          fonte: getString('portal') || 'Effecti',
          observacoes: getString('observacoes') || undefined,
          // mapear todos os anexos para exibição na UI (nome + url)
          attachments: Array.isArray(getRaw('anexos'))
            ? (getRaw('anexos') as unknown[]).map((a) => {
                const rec = a as Record<string, unknown>;
                const nome = typeof rec['nome'] === 'string' && rec['nome'].trim() !== '' ? rec['nome'] as string : (typeof rec['name'] === 'string' ? rec['name'] as string : 'Anexo');
                const url = typeof rec['url'] === 'string' ? rec['url'] as string : (typeof rec['link'] === 'string' ? rec['link'] as string : '');
                return {
                  id: nome || url || String(Math.random()).slice(2, 8),
                  name: nome,
                  url,
                  uploadedAt: getString('dataCaptura') || '',
                };
              })
            : undefined,
        };
      });

      setTenders(mapped);
    } catch (error) {
      // Mantemos o log para debugging em ambiente de desenvolvimento
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar licitações externas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = async (profile: any) => {
    try {
      const response = await fetch(`/api/pncp/search-profiles/${profile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      if (!response.ok) throw new Error(`Erro ${response.status}`);
      await loadProfiles();
      await showAlert('Perfil PNCP salvo.', 'Sucesso');
    } catch (error) {
      console.error('Erro ao salvar perfil PNCP:', error);
      await showAlert('Erro ao salvar perfil PNCP.', 'Erro');
    }
  };

  const showPncpResult = async (result: any) => {
    const errorSummary = Array.isArray(result.errors) && result.errors.length
      ? ` Falhas temporárias: ${result.errors.join(' | ')}`
      : '';
    const coverageSummary = Number(result.pagesAvailable || result.pagesTarget || 0) > 0
      ? ` ${result.pagesProcessed || 0}/${result.pagesAvailable || result.pagesTarget} página(s) do índice processada(s), cobertura real ${result.coveragePercent || 0}%.`
      : '';
    const staleCacheSummary = Number(result.staleCacheHits || 0) > 0
      ? ` ${result.staleCacheHits} página(s) foram reaproveitadas do cache de contingência porque o PNCP estava indisponível.`
      : '';
    const sourceSummary = ` ${result.cacheHits || 0} página(s) reutilizada(s) do banco e ${result.networkRequests || 0} consultada(s) no PNCP.${staleCacheSummary}`;
    if (mountedRef.current) {
      const newOpportunities = Number(result.newOpportunities || 0);
      const revalidatedOpportunities = Number(result.revalidatedOpportunities || 0);
      setPncpSummary(`${newOpportunities} nova(s) oportunidade(s) adicionada(s) aos cards e ${revalidatedOpportunities} oportunidade(s) já existente(s) revalidada(s). ${result.matched || 0} correspondência(s) qualificadas, ${result.fetched || 0} registro(s) examinados.${coverageSummary}${sourceSummary} ${result.skippedLowQuality || 0} correspondência(s) abaixo da qualidade mínima, ${result.skippedClosed || 0} fora de disputa e ${result.skippedInvalid || 0} sem prazo ignorada(s).${errorSummary}`);
    }
    await Promise.all([loadProfiles(), loadSearchRuns(), loadExternalTenders()]);
  };

  const monitorPncpSearch = async () => {
    if (pollingPncpRef.current) return;
    pollingPncpRef.current = true;
    if (mountedRef.current) setRunningPncp(true);

    try {
      // A varredura nacional completa respeita o limite de chamadas do PNCP e
      // pode levar cerca de uma hora.
      for (let attempt = 0; attempt < 2160 && mountedRef.current; attempt += 1) {
        const response = await fetch('/api/pncp/search/status');
        const job = await response.json();
        if (!response.ok) throw new Error(job?.error || `Erro ${response.status}`);

        if (job.status === 'SUCCESS') {
          await showPncpResult(job.result || {});
          return;
        }
        if (job.status === 'ERROR') {
          throw new Error(job.error || 'Erro ao executar busca PNCP.');
        }
        if (job.status === 'IDLE') return;

        const progress = job.progress || {};
        const pageTotal = progress.pagesAvailable || progress.pagesTarget || '?';
        setPncpSummary(
          `Busca em andamento: ${progress.pagesProcessed || 0}/${pageTotal} página(s) do índice, `
          + `${progress.fetched || 0} registro(s) examinados, ${progress.newOpportunities || 0} nova(s) e ${progress.revalidatedOpportunities || 0} revalidada(s)`
          + `${progress.pagesAvailable ? ` · cobertura ${progress.coveragePercent || 0}%` : ''}`
          + ` · ${progress.cacheHits || 0} cache (${progress.staleCacheHits || 0} contingência) / ${progress.networkRequests || 0} rede`
          + `${progress.currentModality ? ` · ${progress.currentModality}, página ${progress.currentPage || 1}` : ''}.`
          + `${progress.lastError ? ` Última falha: ${progress.lastError}` : ''}`,
        );
        if (attempt > 0 && attempt % 6 === 0) {
          await Promise.all([loadExternalTenders(), loadViewCounts()]);
        }
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
      if (mountedRef.current) throw new Error('A busca continua no servidor. Acompanhe novamente em alguns minutos.');
    } catch (error) {
      console.error('Erro ao acompanhar busca PNCP:', error);
      if (mountedRef.current) {
        setPncpSummary(error instanceof Error ? error.message : 'Erro ao acompanhar busca PNCP.');
      }
    } finally {
      pollingPncpRef.current = false;
      if (mountedRef.current) setRunningPncp(false);
    }
  };

  const resumePncpSearch = async () => {
    try {
      const response = await fetch('/api/pncp/search/status');
      const job = await response.json();
      if (response.ok && job.status === 'RUNNING') await monitorPncpSearch();
    } catch (error) {
      console.error('Erro ao verificar busca PNCP em andamento:', error);
    }
  };

  const runPncpSearch = async () => {
    setRunningPncp(true);
    setPncpSummary('Iniciando busca PNCP em segundo plano...');
    try {
      const response = await fetch('/api/pncp/search/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const job = await response.json();
      if (!response.ok) throw new Error(job?.error || `Erro ${response.status}`);
      await monitorPncpSearch();
    } catch (error) {
      console.error('Erro ao executar busca PNCP:', error);
      setPncpSummary(error instanceof Error ? error.message : 'Erro ao executar busca PNCP.');
      setRunningPncp(false);
    }
  };

  const handleArchive = async (t: ExternalTender) => {
    if (!t.id) return;
    const conf = await showConfirm(`Deseja descartar a oportunidade ${t.edital || t.id} por não corresponder ao objeto, parâmetros ou interesse da empresa? Ela será preservada no histórico de Descartadas.`, 'Descartar por falta de aderência');
    if (!conf) return;

    try {
      setDiscardingId(t.id);
      const response = await fetch(`/api/tenders/external/${encodeURIComponent(t.id)}/archive`, { method: 'PUT' });
      if (!response.ok) {
        const txt = await response.text().catch(() => '');
        throw new Error(txt || `Erro ${response.status}`);
      }
      setTenders(prev => prev.filter(item => item.id !== t.id));
      void loadViewCounts();
      await showAlert('Oportunidade descartada com sucesso.', 'Sucesso');
    } catch (err: unknown) {
      console.error('Erro ao descartar oportunidade:', err);
      try {
        await showAlert('Erro ao descartar oportunidade. Verifique o console para mais detalhes.', 'Erro');
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Erro ao descartar oportunidade. Verifique o console para mais detalhes.');
      }
    } finally {
      setDiscardingId(null);
    }
  };

  const handleBatchAction = async (action: BatchAction) => {
    const ids: string[] = [...selectedTenderIds];
    if (!ids.length || batchAction) return;

    const confirmation: Record<BatchAction, { title: string; message: string }> = {
      FAVORITE: {
        title: 'Adicionar oportunidades à participação',
        message: `Deseja adicionar ${ids.length} oportunidade(s) a Licitações em Participação?`,
      },
      ARCHIVE: {
        title: 'Descartar oportunidades selecionadas',
        message: `Deseja descartar ${ids.length} oportunidade(s) por não corresponderem ao objeto, parâmetros ou interesse da empresa? Elas serão preservadas no histórico de Descartadas.`,
      },
      PARTICIPATE: {
        title: 'Confirmar participação em lote',
        message: `Confirma que a empresa pretende participar de ${ids.length} oportunidade(s) selecionada(s)?`,
      },
      DO_NOT_PARTICIPATE: {
        title: 'Registrar não participação em lote',
        message: `Confirma a decisão interna de não participar de ${ids.length} oportunidade(s)? Elas continuarão registradas para histórico e acompanhamento.`,
      },
      RESTORE: {
        title: 'Restaurar oportunidades selecionadas',
        message: `Deseja restaurar ${ids.length} oportunidade(s)? Elas retornarão para a busca ativa ou para Licitações em Participação quando já houver registro vinculado.`,
      },
    };
    const confirmed = await showConfirm(
      confirmation[action].message,
      confirmation[action].title,
    );
    if (!confirmed) return;

    try {
      setBatchAction(action);
      let completed = 0;
      let errors: Array<{ id: string; error: string }> = [];
      let message = '';

      if (action === 'FAVORITE') {
        const result = await apiService.favoriteExternalTenders(ids);
        completed = result.favorited + result.alreadyFavorited;
        errors = result.errors;
        message = `${completed} oportunidade(s) enviada(s) para Licitações em andamento.`;
      } else if (action === 'ARCHIVE') {
        const result = await apiService.archiveExternalTenders(ids);
        completed = result.archived;
        errors = result.errors;
        message = `${completed} oportunidade(s) descartada(s) e preservada(s) no histórico.`;
      } else if (action === 'RESTORE') {
        const result = await apiService.restoreExternalTenders(ids);
        completed = result.restored;
        errors = result.errors;
        message = `${completed} oportunidade(s) restaurada(s).`;
      } else {
        const result = await apiService.updateExternalTenderParticipation(
          ids,
          action === 'PARTICIPATE' ? 'PARTICIPATE' : 'DO_NOT_PARTICIPATE',
        );
        completed = result.updated;
        errors = result.errors;
        message = action === 'PARTICIPATE'
          ? `${completed} oportunidade(s) marcada(s) para participação.`
          : `${completed} oportunidade(s) marcada(s) como não participar.`;
      }

      setSelectedTenderIds(new Set());
      await Promise.all([loadExternalTenders(opportunityView), loadViewCounts()]);
      const failureSuffix = errors.length
        ? ` ${errors.length} item(ns) não foram alterados porque já não estavam disponíveis nesta lista.`
        : '';
      await showAlert(
        `${message}${failureSuffix}`,
        errors.length ? 'Ação concluída parcialmente' : 'Sucesso',
      );
    } catch (error) {
      console.error('Erro ao executar ação em lote:', error);
      await showAlert(error instanceof Error ? error.message : 'Erro ao executar a ação nas oportunidades selecionadas.', 'Erro');
    } finally {
      setBatchAction(null);
    }
  };

  const toggleRead = async (tender: ExternalTender) => {
    if (reviewingId) return;
    try {
      setReviewingId(tender.id);
      const response = await fetch(`/api/tenders/external/${encodeURIComponent(tender.id)}/read`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ read: !tender.readAt }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Erro ${response.status}`);
      setTenders((current) => current.map((item) => item.id === tender.id
        ? { ...item, readAt: data.readAt || null, readByName: data.readByName || null }
        : item));
      void loadViewCounts();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao atualizar leitura.', 'Erro');
    } finally {
      setReviewingId(null);
    }
  };

  const restoreTender = async (tender: ExternalTender) => {
    if (restoringId) return;
    const confirmed = await showConfirm(
      `Deseja restaurar a oportunidade ${tender.edital || tender.id} para a busca ativa?`,
      'Restaurar oportunidade',
    );
    if (!confirmed) return;
    try {
      setRestoringId(tender.id);
      const response = await fetch(`/api/tenders/external/${encodeURIComponent(tender.id)}/restore`, { method: 'PUT' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Erro ${response.status}`);
      setTenders((current) => current.filter((item) => item.id !== tender.id));
      void loadViewCounts();
      await showAlert(
        data.destination === 'FAVORITED'
          ? 'Oportunidade restaurada para Licitações em Participação.'
          : 'Oportunidade restaurada para a busca ativa.',
        'Sucesso',
      );
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao restaurar oportunidade.', 'Erro');
    } finally {
      setRestoringId(null);
    }
  };

  const declineFavorite = async (tender: ExternalTender) => {
    if (decidingId || !tender.favoriteTenderId) return;
    const confirmed = await showConfirm(
      `Confirma a decisão interna de não participar da oportunidade ${tender.edital || tender.id}? Ela irá para Participações arquivadas e poderá ter o resultado acompanhado.`,
      'Não participar',
    );
    if (!confirmed) return;

    try {
      setDecidingId(tender.id);
      const updated = await apiService.updateTenderQualification(tender.favoriteTenderId, {
        priority: 'NO_GO',
        participationDecision: 'DO_NOT_PARTICIPATE',
      });
      setTenders((current) => opportunityView === 'FAVORITED'
        ? current.filter((item) => item.id !== tender.id)
        : current.map((item) => item.id === tender.id ? {
        ...item,
        favoritePriority: updated.priority,
        favoriteParticipationDecision: updated.participationDecision,
      } : item));
      void loadViewCounts();
      await showAlert('Decisão registrada. A oportunidade foi enviada para Participações arquivadas.', 'Sucesso');
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao registrar a decisão.', 'Erro');
    } finally {
      setDecidingId(null);
    }
  };

  const participateFavorite = async (tender: ExternalTender) => {
    if (decidingId || !tender.favoriteTenderId) return;
    try {
      setDecidingId(tender.id);
      const updated = await apiService.updateTenderQualification(tender.favoriteTenderId, {
        priority: tender.favoritePriority === 'NO_GO' ? 'NONE' : tender.favoritePriority || 'NONE',
        participationDecision: 'PARTICIPATE',
      });
      setTenders((current) => opportunityView === 'ARCHIVED'
        ? current.filter((item) => item.id !== tender.id)
        : current.map((item) => item.id === tender.id ? {
          ...item,
          favoritePriority: updated.priority,
          favoriteParticipationDecision: updated.participationDecision,
        } : item));
      void loadViewCounts();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao registrar participação.', 'Erro');
    } finally {
      setDecidingId(null);
    }
  };

  // prefer external controllers when provided
  const effectiveSearch = typeof externalSearchTerm === 'string' ? externalSearchTerm : localSearchTerm;
  const effectiveState = typeof externalStateFilter === 'string' ? externalStateFilter : localStateFilter;
  const availableSources = useMemo(() => {
    return Array.from(new Set(tenders.map((tender) => tender.portal || tender.fonte || 'Sem fonte').filter(Boolean))).sort();
  }, [tenders]);

  const availableCategories = useMemo(() => {
    const fromTenders = tenders.flatMap((tender) => tender.terms || []).map((term) => ({
      value: term.term,
      label: term.label || term.term,
    }));
    const fromProfiles = profiles.map((profile) => ({
      value: String(profile.category || ''),
      label: String(profile.label || profile.category || ''),
    }));
    return Array.from(new Map([...fromProfiles, ...fromTenders].filter((item) => item.value).map((item) => [item.value, item])).values())
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [profiles, tenders]);

  const dailySearchIndex = useMemo(() => {
    const grouped = new Map<string, any[]>();
    for (const run of searchRuns) {
      const date = String(run.searchedAt || run.createdAt || '').slice(0, 10) || 'sem-data';
      grouped.set(date, [...(grouped.get(date) || []), run]);
    }
    return Array.from(grouped.entries()).map(([date, runs]) => ({
      date,
      runs,
      saved: runs.reduce((total, run) => total + Number(run.saved || 0), 0),
      matched: runs.reduce((total, run) => total + Number(run.matched || 0), 0),
    }));
  }, [searchRuns]);

  const filteredTenders = tenders.filter(tender => {
    const source = tender.portal || tender.fonte || 'Sem fonte';
    const normalizedSearch = normalizeFilterText(effectiveSearch);
    const searchableText = normalizeFilterText([
      tender.orgao,
      tender.objeto,
      tender.edital,
      tender.cidade,
      tender.estado,
      tender.modalidade,
      source,
      tender.uasg,
    ].filter(Boolean).join(' '));
    const matchesSearch = !normalizedSearch || searchableText.includes(normalizedSearch);

    const matchesState = effectiveState === 'ALL'
      || normalizeFilterText(tender.estado) === normalizeFilterText(effectiveState);
    const matchesSource = sourceFilter === 'ALL'
      || normalizeFilterText(source) === normalizeFilterText(sourceFilter);
    const matchesCategory = categoryFilter === 'ALL'
      || normalizeFilterText(tender.matchDetails?.category) === normalizeFilterText(categoryFilter)
      || (tender.terms || []).some((term) => normalizeFilterText(term.term) === normalizeFilterText(categoryFilter));
    const tenderModality = normalizeFilterText(tender.modalidade);
    const matchesModality = modalityFilter === 'ALL'
      || (modalityFilter === 'PREGAO' && tenderModality.includes('preg'))
      || (modalityFilter === 'CONCORRENCIA' && tenderModality.includes('concorr'))
      || (modalityFilter === 'DISPENSA' && tenderModality.includes('dispensa'))
      || (modalityFilter === 'CREDENCIAMENTO' && tenderModality.includes('credenciamento'))
      || (modalityFilter === 'CHAMADA_PUBLICA' && tenderModality.includes('chamada'));
    const minimumQuality = qualityFilter === 'ALL' ? 0 : Number(qualityFilter);
    const matchesQuality = qualityFilter === 'ALL'
      || (typeof tender.relevanceScore === 'number' && tender.relevanceScore >= minimumQuality);
    const matchesRead = readFilter === 'ALL'
      || (readFilter === 'READ' && Boolean(tender.readAt))
      || (readFilter === 'UNREAD' && !tender.readAt);
    const deadline = parseExternalDeadline(tender.encerramento);
    const daysRemaining = getExternalDaysRemaining(tender.encerramento);
    const fromTimestamp = dateInputTimestamp(deadlineFrom);
    const toTimestamp = dateInputTimestamp(deadlineTo, true);
    const matchesDeadlineWindow = deadlineWindow === 'ALL'
      || (daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= Number(deadlineWindow));
    const matchesDeadlineFrom = fromTimestamp === null || (deadline !== null && deadline.getTime() >= fromTimestamp);
    const matchesDeadlineTo = toTimestamp === null || (deadline !== null && deadline.getTime() <= toTimestamp);

    return matchesSearch
      && matchesState
      && matchesSource
      && matchesCategory
      && matchesModality
      && matchesQuality
      && matchesRead
      && matchesDeadlineWindow
      && matchesDeadlineFrom
      && matchesDeadlineTo;
  }).sort((a, b) => {
    if (deadlineSort === 'QUALITY_DESC') return Number(b.relevanceScore || 0) - Number(a.relevanceScore || 0);
    if (deadlineSort === 'NEWEST') {
      const aIncluded = Date.parse(a.incluido || '');
      const bIncluded = Date.parse(b.incluido || '');
      const aTimestamp = Number.isNaN(aIncluded) ? 0 : aIncluded;
      const bTimestamp = Number.isNaN(bIncluded) ? 0 : bIncluded;
      return bTimestamp - aTimestamp;
    }
    const aDeadline = parseExternalDeadline(a.encerramento)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bDeadline = parseExternalDeadline(b.encerramento)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return aDeadline - bDeadline;
  });

  const totalPages = Math.max(1, Math.ceil(filteredTenders.length / OPPORTUNITIES_PER_PAGE));
  const pageStart = (currentPage - 1) * OPPORTUNITIES_PER_PAGE;
  const paginatedTenders = filteredTenders.slice(pageStart, pageStart + OPPORTUNITIES_PER_PAGE);
  const pageTenderIds = paginatedTenders.map((tender) => tender.id);
  const selectedTenderCount = selectedTenderIds.size;
  const allPageTendersSelected = pageTenderIds.length > 0 && pageTenderIds.every((id) => selectedTenderIds.has(id));

  const toggleTenderSelection = (id: string) => {
    setSelectedTenderIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePageSelection = () => {
    setSelectedTenderIds((current) => {
      const next = new Set(current);
      if (allPageTendersSelected) pageTenderIds.forEach((id) => next.delete(id));
      else pageTenderIds.forEach((id) => next.add(id));
      return next;
    });
  };

  useEffect(() => {
    setCurrentPage(1);
    setSelectedTenderIds(new Set());
  }, [effectiveSearch, effectiveState, sourceFilter, categoryFilter, modalityFilter, qualityFilter, readFilter, deadlineWindow, deadlineFrom, deadlineTo, deadlineSort, opportunityView]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    setSelectedTenderIds(new Set());
  }, [currentPage]);

  const openTenderDocument = async (tender: ExternalTender, directUrl?: string) => {
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      await showAlert('O navegador bloqueou a nova guia. Permita pop-ups para abrir o edital.', 'Atenção');
      return;
    }
    popup.opener = null;

    try {
      let targetUrl = directUrl || '';
      if (!targetUrl) {
        const response = await fetch(`/api/tenders/external/${encodeURIComponent(tender.id)}/edital-url`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok || typeof data.url !== 'string') {
          throw new Error(data.error || 'Edital não disponível');
        }
        targetUrl = data.url;
      }

      if (!/^https?:\/\//i.test(targetUrl)) throw new Error('URL do edital inválida');
      popup.location.replace(targetUrl);
    } catch (error) {
      popup.close();
      console.error('Erro ao abrir edital:', error);
      await showAlert(error instanceof Error ? error.message : 'Erro ao abrir o edital.', 'Erro');
    }
  };

  // Debug info removed for cleaner console output in production

  const getDaysRemaining = (encerramento: string) => {
    try {
      return getExternalDaysRemaining(encerramento);
    } catch {
      return null;
    }
  };

  const formatDate = (raw?: string) => {
    if (!raw) return '-';
    // If already dd/MM/yyyy, return as-is
    if (/^\d{2}\/\d{2}\/\d{4}/.test(raw)) return raw;
    // Datas sem horário representam um dia local e não devem recuar pelo fuso.
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [yyyy, mm, dd] = raw.split('-');
      return `${dd}/${mm}/${yyyy}`;
    }
    // Try ISO parse
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) {
      const d = new Date(parsed);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
    return raw;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const sanitizeHtml = (raw?: string) => {
    if (!raw) return '';
    // Allow common tags and mark/em for highlights; keep links but remove scripts/styles
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'mark', 'p', 'br', 'ul', 'ol', 'li', 'a', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    });
  };

  const exportViewLabel = opportunityView === 'ACTIVE'
    ? 'Ativas'
    : opportunityView === 'FAVORITED'
    ? 'Em participação'
    : opportunityView === 'ARCHIVED'
    ? 'Participações arquivadas'
    : 'Descartadas';

  const exportFilterSummary = [
    `Situação: ${exportViewLabel}`,
    effectiveSearch ? `Texto: ${effectiveSearch}` : null,
    effectiveState !== 'ALL' ? `UF: ${effectiveState}` : null,
    sourceFilter !== 'ALL' ? `Fonte: ${sourceFilter}` : null,
    categoryFilter !== 'ALL' ? `Categoria: ${availableCategories.find((item) => item.value === categoryFilter)?.label || categoryFilter}` : null,
    modalityFilter !== 'ALL' ? `Modalidade: ${modalityFilter}` : null,
    qualityFilter !== 'ALL' ? `Qualidade mínima: ${qualityFilter}` : null,
    readFilter !== 'ALL' ? `Leitura: ${readFilter === 'READ' ? 'Lidas' : 'Não lidas'}` : null,
    deadlineWindow !== 'ALL' ? `Encerra em até ${deadlineWindow} dia(s)` : null,
    deadlineFrom ? `Encerramento desde: ${formatDate(deadlineFrom)}` : null,
    deadlineTo ? `Encerramento até: ${formatDate(deadlineTo)}` : null,
  ].filter((value): value is string => Boolean(value));

  const plainText = (value?: string) => {
    if (!value) return '';
    const document = new DOMParser().parseFromString(value, 'text/html');
    return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const exportRows = filteredTenders.map((tender) => ({
    edital: tender.edital || '',
    id: tender.id || '',
    orgao: tender.orgao || '',
    objeto: plainText(tender.objeto),
    modalidade: tender.modalidade || '',
    uf: tender.estado || '',
    cidade: tender.cidade || '',
    recebimentoPropostas: formatDate(tender.recebimentoPropostas),
    encerramento: formatDate(tender.encerramento),
    diasRestantes: getExternalDaysRemaining(tender.encerramento),
    publicacao: formatDate(tender.incluido),
    fonte: tender.portal || tender.fonte || '',
    uasg: tender.uasg ?? '',
    perfil: tender.matchDetails?.profile || tender.perfilNome || '',
    qualidade: tender.relevanceScore ?? '',
    nivelQualidade: tender.relevanceLevel || '',
    termos: tender.matchDetails?.matchedTerms?.join(', ') || tender.terms?.map((term) => term.label || term.term).join(', ') || '',
    modeloComercial: tender.matchDetails?.businessModels?.join(', ') || '',
    valorTotal: tender.valorTotal ?? '',
    quantidadeItens: tender.itens?.length || 0,
    itens: tender.itens?.map((item) => plainText(item.descricao)).filter(Boolean).join(' | ') || '',
    lida: tender.readAt ? 'Sim' : 'Não',
    url: tender.urlEdital || tender.portalUrl || '',
  }));

  const downloadExport = (content: BlobPart, mimeType: string, extension: string) => {
    const date = new Date().toISOString().slice(0, 10);
    const view = opportunityView.toLocaleLowerCase('pt-BR');
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `oportunidades-${view}-${date}.${extension}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const escapeCsv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const escapeXml = (value: unknown) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

  const exportCsv = () => {
    const columns: Array<[keyof (typeof exportRows)[number], string]> = [
      ['edital', 'Edital'], ['id', 'ID'], ['orgao', 'Órgão'], ['objeto', 'Objeto'],
      ['modalidade', 'Modalidade'], ['uf', 'UF'], ['cidade', 'Cidade'],
      ['recebimentoPropostas', 'Início das propostas'], ['encerramento', 'Encerramento'],
      ['diasRestantes', 'Dias restantes'], ['publicacao', 'Publicação'], ['fonte', 'Fonte'],
      ['uasg', 'UASG'], ['perfil', 'Perfil'], ['qualidade', 'Qualidade'],
      ['nivelQualidade', 'Nível de qualidade'], ['termos', 'Termos encontrados'],
      ['modeloComercial', 'Modelo comercial'], ['valorTotal', 'Valor total'],
      ['quantidadeItens', 'Quantidade de itens'], ['itens', 'Itens'], ['lida', 'Lida'], ['url', 'URL'],
    ];
    const lines = [
      columns.map(([, label]) => escapeCsv(label)).join(';'),
      ...exportRows.map((row) => columns.map(([key]) => escapeCsv(row[key])).join(';')),
    ];
    downloadExport(`\uFEFF${lines.join('\r\n')}`, 'text/csv;charset=utf-8', 'csv');
  };

  const exportXml = () => {
    const filters = exportFilterSummary.map((filter) => `    <filtro>${escapeXml(filter)}</filtro>`).join('\n');
    const opportunities = exportRows.map((row) => `  <oportunidade>\n${Object.entries(row)
      .map(([key, value]) => `    <${key}>${escapeXml(value)}</${key}>`)
      .join('\n')}\n  </oportunidade>`).join('\n');
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<resultadoBusca geradoEm="${escapeXml(new Date().toISOString())}" total="${exportRows.length}">\n  <filtros>\n${filters}\n  </filtros>\n${opportunities}\n</resultadoBusca>\n`;
    downloadExport(xml, 'application/xml;charset=utf-8', 'xml');
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    let y = 16;

    const ensureSpace = (height: number) => {
      if (y + height <= pageHeight - 14) return;
      doc.addPage();
      y = 16;
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Relatório de oportunidades', margin, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')} · ${exportRows.length} resultado(s)`, margin, y);
    y += 5;
    const filterLines = doc.splitTextToSize(exportFilterSummary.join(' · '), contentWidth) as string[];
    doc.text(filterLines, margin, y);
    y += filterLines.length * 4 + 4;

    exportRows.forEach((row, index) => {
      const title = `${index + 1}. ${row.edital || row.id} · ${row.orgao || 'Órgão não informado'}`;
      const location = [row.cidade, row.uf].filter(Boolean).join(' / ') || '-';
      const details = [
        `Modalidade: ${row.modalidade || '-'} | Local: ${location}`,
        `Encerramento: ${row.encerramento || '-'} | Dias restantes: ${row.diasRestantes ?? '-'} | Qualidade: ${row.qualidade || '-'}`,
        `Fonte: ${row.fonte || '-'} | Valor: ${typeof row.valorTotal === 'number' ? formatCurrency(row.valorTotal) : '-'}`,
      ];
      const objectLines = doc.splitTextToSize(`Objeto: ${row.objeto || '-'}`, contentWidth) as string[];
      const itemLines = row.itens ? doc.splitTextToSize(`Itens: ${row.itens}`, contentWidth) as string[] : [];
      const blockHeight = 6 + details.length * 4 + objectLines.length * 4 + Math.min(itemLines.length, 4) * 4 + 5;
      ensureSpace(blockHeight);

      doc.setDrawColor(210, 214, 220);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(doc.splitTextToSize(title, contentWidth), margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      details.forEach((detail) => {
        doc.text(detail, margin, y);
        y += 4;
      });
      doc.text(objectLines, margin, y);
      y += objectLines.length * 4;
      if (itemLines.length > 0) {
        doc.text(itemLines.slice(0, 4), margin, y);
        y += Math.min(itemLines.length, 4) * 4;
      }
      y += 3;
    });

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(`Página ${page} de ${pageCount}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
    }
    doc.save(`oportunidades-${opportunityView.toLocaleLowerCase('pt-BR')}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleExport = (format: ExportFormat) => {
    setExportMenuOpen(false);
    if (filteredTenders.length === 0) {
      void showAlert('Não há resultados nos filtros atuais para exportar.', 'Atenção');
      return;
    }
    if (format === 'CSV') exportCsv();
    if (format === 'XML') exportXml();
    if (format === 'PDF') exportPdf();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-300 animate-spin" />
        <span className="ml-3 text-ink-subtitle dark:text-ink-subtitle-dark">Carregando oportunidades...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 border-b border-line dark:border-line-dark px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
              <Globe className="w-4 h-4 text-brand-600" />
              Busca de oportunidades PNCP
            </h3>
            <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
              Busca diária por oportunidades futuras. O PNCP está ativo; demais portais podem alimentar a lista por fonte quando conectados.
            </p>
          </div>
          <button type="button" onClick={runPncpSearch} disabled={runningPncp} className="btn-primary">
            {runningPncp ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Buscar PNCP agora
          </button>
        </div>
        <div className="p-4 space-y-3">
          {pncpSummary && (
            <div className="rounded-md border border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark">
              {pncpSummary}
            </div>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            {profiles.map((profile, index) => (
              <div key={profile.id} className="rounded-md border border-line dark:border-line-dark p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <input
                    className="input font-semibold"
                    value={profile.label || ''}
                    onChange={(e) => setProfiles(prev => prev.map((p, i) => i === index ? { ...p, label: e.target.value } : p))}
                  />
                  <label className="flex items-center gap-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                    <input
                      type="checkbox"
                      checked={profile.enabled}
                      onChange={(e) => setProfiles(prev => prev.map((p, i) => i === index ? { ...p, enabled: e.target.checked } : p))}
                      className="accent-brand-600"
                    />
                    Ativo
                  </label>
                </div>
                <textarea
                  className="input min-h-[120px] resize-y"
                  value={Array.isArray(profile.keywords) ? profile.keywords.join('\n') : profile.keywords || ''}
                  onChange={(e) => setProfiles(prev => prev.map((p, i) => i === index ? { ...p, keywords: e.target.value.split(/\n|,|;/).map(v => v.trim()).filter(Boolean) } : p))}
                />
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                    Janela de oportunidade
                    <div className="input mt-1 flex items-center bg-surface-alt dark:bg-surface-dark-alt">45 dias futuros</div>
                  </div>
                  <label className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                    Páginas por modalidade
                    <input
                      className="input mt-1"
                      type="number"
                      min={1}
                      max={1000}
                      value={profile.maxPages || 500}
                      onChange={(e) => setProfiles(prev => prev.map((p, i) => i === index ? { ...p, maxPages: Number(e.target.value) } : p))}
                    />
                  </label>
                  <label className="col-span-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                    Qualidade mínima
                    <input
                      className="input mt-1"
                      type="number"
                      min={0}
                      max={100}
                      value={profile.minimumScore ?? 50}
                      onChange={(e) => setProfiles(prev => prev.map((p, i) => i === index ? { ...p, minimumScore: Number(e.target.value) } : p))}
                    />
                  </label>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">
                      {profile.lastRunAt ? `Última busca: ${formatDate(profile.lastRunAt)} · ${profile.lastRunStatus || '-'}` : 'Ainda não executado'}
                    </div>
                    {profile.lastRunMessage && (
                      <div className="mt-0.5 truncate text-[11px] text-ink-placeholder dark:text-ink-placeholder-dark" title={profile.lastRunMessage}>
                        {profile.lastRunMessage}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => saveProfile(profile)} className="icon-btn" title="Salvar perfil">
                    <Save className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-md border border-line dark:border-line-dark overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-surface-alt dark:bg-surface-dark-alt">
              <div className="flex items-center gap-2 text-sm font-semibold text-ink-title dark:text-ink-title-dark">
                <CalendarDays className="w-4 h-4 text-brand-600" />
                Índice diário de buscas PNCP
              </div>
              <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Últimas 30 execuções</span>
            </div>
            {dailySearchIndex.length === 0 ? (
              <div className="px-3 py-4 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Nenhuma execução registrada nesta instalação.</div>
            ) : (
              <div className="divide-y divide-line dark:divide-line-dark">
                {dailySearchIndex.map((day) => (
                  <div key={day.date} className="px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="font-medium text-ink-title dark:text-ink-title-dark">{formatDate(day.date)}</span>
                      <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{day.saved} vínculo(s) por perfil em {day.matched} correspondência(s)</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {day.runs.map((run: any) => (
                        <span key={run.id} className={`rounded border px-2 py-0.5 text-[11px] ${run.status === 'SUCCESS' ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300' : 'border-amber-200 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'}`} title={run.message || ''}>
                          {run.profile?.label || run.profile?.category || 'Perfil'}: {run.saved || 0} oportunidade(s) em {run.fetched || 0} registro(s) · {run.modalities || 'PNCP'}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Filtros */}
      <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2 border-b border-line dark:border-line-dark pb-3">
          {([
            ['ACTIVE', 'Ativas', viewCounts.active],
            ['FAVORITED', 'Em participação', viewCounts.favorited],
            ['ARCHIVED', 'Participações arquivadas', viewCounts.archived],
            ['DISCARDED', 'Descartadas', viewCounts.discarded],
          ] as Array<[OpportunityView, string, number]>).map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              onClick={() => setOpportunityView(value)}
              className={`h-9 px-3 rounded-md border text-sm font-medium flex items-center gap-2 ${
                opportunityView === value
                  ? 'border-brand-500 bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300'
                  : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt'
              }`}
            >
              {value === 'ACTIVE' ? <Grid className="w-4 h-4" /> : value === 'FAVORITED' ? <BookmarkCheck className="w-4 h-4" /> : value === 'ARCHIVED' ? <Archive className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
              {label}
              <span className="min-w-5 h-5 px-1 rounded bg-surface-alt dark:bg-surface-dark-alt text-[11px] grid place-items-center">{count}</span>
            </button>
          ))}
          <div className="relative ml-auto">
            <button
              type="button"
              onClick={() => setExportMenuOpen((open) => !open)}
              disabled={filteredTenders.length === 0}
              className={`${theme.classes.button.outline} h-9 px-3 text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
              title="Exportar todos os resultados conforme os filtros aplicados"
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen}
            >
              <Download className="w-4 h-4" />
              Exportar
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {exportMenuOpen && (
              <div role="menu" className="absolute right-0 z-20 mt-1 w-44 rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-1 shadow-lg">
                {(['CSV', 'XML', 'PDF'] as ExportFormat[]).map((format) => (
                  <button
                    key={format}
                    type="button"
                    role="menuitem"
                    onClick={() => handleExport(format)}
                    className="w-full rounded px-3 py-2 text-left text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt"
                  >
                    Exportar em {format}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCategoryFilter('ALL')}
            className={`px-3 py-1.5 rounded-md border text-xs font-medium ${categoryFilter === 'ALL' ? 'border-brand-500 bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300' : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'}`}
          >
            Todas as categorias
          </button>
          {availableCategories.map((category) => (
            <button
              key={category.value}
              type="button"
              onClick={() => setCategoryFilter(category.value)}
              className={`px-3 py-1.5 rounded-md border text-xs font-medium ${categoryFilter === category.value ? 'border-brand-500 bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] text-brand-700 dark:text-brand-300' : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'}`}
            >
              {category.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
          </div>
          <input
            type="text"
            placeholder="Buscar por órgão, objeto ou edital..."
            value={typeof externalSearchTerm === 'string' ? externalSearchTerm : localSearchTerm}
            onChange={(e) => {
              if (externalSetSearchTerm) return externalSetSearchTerm(e.target.value);
              setLocalSearchTerm(e.target.value);
            }}
            className="w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 block pl-10 p-2.5 outline-none"
          />
        </div>
        <select
          aria-label="Filtrar por UF"
          value={typeof externalStateFilter === 'string' ? externalStateFilter : localStateFilter}
          onChange={(e) => {
            if (externalSetStateFilter) return externalSetStateFilter(e.target.value);
            setLocalStateFilter(e.target.value);
          }}
          className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 block px-3 py-2.5 outline-none min-w-[140px]"
        >
          <option value="ALL">UF: Todas</option>
          {BRAZIL_STATES.map(uf => (
            <option key={uf} value={uf}>{uf}</option>
          ))}
        </select>
        <select
          aria-label="Filtrar por fonte"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 block px-3 py-2.5 outline-none min-w-[180px]"
        >
          <option value="ALL">Fonte: Todas</option>
          {availableSources.map(source => (
            <option key={source} value={source}>{source}</option>
          ))}
        </select>
        <select
          aria-label="Filtrar por modalidade"
          value={modalityFilter}
          onChange={(e) => setModalityFilter(e.target.value)}
          className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 block px-3 py-2.5 outline-none min-w-[160px]"
        >
          <option value="ALL">Modalidade: Todas</option>
          <option value="PREGAO">Pregões</option>
          <option value="CONCORRENCIA">Concorrências</option>
          <option value="DISPENSA">Dispensas</option>
          <option value="CREDENCIAMENTO">Credenciamentos</option>
          <option value="CHAMADA_PUBLICA">Chamadas públicas</option>
        </select>
        <select
          aria-label="Filtrar por qualidade"
          value={qualityFilter}
          onChange={(e) => setQualityFilter(e.target.value)}
          className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 block px-3 py-2.5 outline-none min-w-[170px]"
        >
          <option value="ALL">Qualidade: Todas</option>
          <option value="50">Qualidade: Média+</option>
          <option value="70">Qualidade: Alta+</option>
          <option value="85">Qualidade: Muito alta</option>
        </select>
        <div className="flex items-center">
          <ClearFiltersButton onClear={clearFilters} />
        </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 border-t border-line dark:border-line-dark pt-3">
          <label className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
            Leitura
            <select value={readFilter} onChange={(event) => setReadFilter(event.target.value as ReadFilter)} className="input mt-1">
              <option value="ALL">Lidas e não lidas</option>
              <option value="UNREAD">Somente não lidas</option>
              <option value="READ">Somente lidas</option>
            </select>
          </label>
          <label className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
            Encerra em
            <select value={deadlineWindow} onChange={(event) => setDeadlineWindow(event.target.value)} className="input mt-1">
              <option value="ALL">Qualquer prazo</option>
              <option value="0">Hoje</option>
              <option value="3">Até 3 dias</option>
              <option value="7">Até 7 dias</option>
              <option value="15">Até 15 dias</option>
              <option value="30">Até 30 dias</option>
            </select>
          </label>
          <label className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
            Encerramento de
            <input
              type="date"
              value={deadlineFrom}
              onInput={(event) => setDeadlineFrom(event.currentTarget.value)}
              onChange={(event) => setDeadlineFrom(event.target.value)}
              className="input mt-1"
            />
          </label>
          <label className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
            Encerramento até
            <input
              type="date"
              value={deadlineTo}
              onInput={(event) => setDeadlineTo(event.currentTarget.value)}
              onChange={(event) => setDeadlineTo(event.target.value)}
              className="input mt-1"
            />
          </label>
          <label className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
            Ordenar por
            <select value={deadlineSort} onChange={(event) => setDeadlineSort(event.target.value as DeadlineSort)} className="input mt-1">
              <option value="DEADLINE_ASC">Encerramento mais próximo</option>
              <option value="QUALITY_DESC">Maior qualidade</option>
              <option value="NEWEST">Incluídas recentemente</option>
            </select>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 py-2">
          <div className="text-[11px] uppercase text-ink-subtitle dark:text-ink-subtitle-dark">
            {opportunityView === 'ACTIVE' ? 'Oportunidades ativas' : opportunityView === 'FAVORITED' ? 'Licitações em participação' : opportunityView === 'ARCHIVED' ? 'Participações arquivadas' : 'Histórico descartado'}
          </div>
          <div className="text-xl font-semibold text-ink-title dark:text-ink-title-dark">{tenders.length}</div>
        </div>
        <div className="rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 py-2">
          <div className="text-[11px] uppercase text-ink-subtitle dark:text-ink-subtitle-dark">Após filtros</div>
          <div className="text-xl font-semibold text-ink-title dark:text-ink-title-dark">{filteredTenders.length}</div>
        </div>
        <div className="rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 py-2">
          <div className="text-[11px] uppercase text-ink-subtitle dark:text-ink-subtitle-dark">Não lidas ativas</div>
          <div className="text-xl font-semibold text-ink-title dark:text-ink-title-dark">{viewCounts.unread}</div>
        </div>
        <div className="rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 py-2">
          <div className="text-[11px] uppercase text-ink-subtitle dark:text-ink-subtitle-dark">Lidas ativas</div>
          <div className="text-xl font-semibold text-ink-title dark:text-ink-title-dark">{viewCounts.read}</div>
          {autoDiscardedCount > 0 && <div className="text-[10px] text-ink-placeholder">{autoDiscardedCount} descarte(s) automático(s) nesta carga</div>}
        </div>
      </div>

      {paginatedTenders.length > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-ink-text dark:text-ink-text-dark">
            <input
              type="checkbox"
              checked={allPageTendersSelected}
              onChange={togglePageSelection}
              disabled={Boolean(batchAction)}
              className="h-4 w-4 rounded border-line text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
            />
            Selecionar as {paginatedTenders.length} oportunidades desta página
          </label>
          {selectedTenderCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
                {selectedTenderCount} selecionada{selectedTenderCount === 1 ? '' : 's'}
              </span>
              {BATCH_ACTIONS[opportunityView].map(({ action, label, icon: Icon, className }) => (
                <button
                  key={action}
                  type="button"
                  onClick={() => void handleBatchAction(action)}
                  disabled={Boolean(batchAction)}
                  className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
                >
                  {batchAction === action ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
                  {label}
                </button>
              ))}
              <button
                type="button"
                aria-label="Limpar seleção"
                title="Limpar seleção"
                onClick={() => setSelectedTenderIds(new Set())}
                disabled={Boolean(batchAction)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line text-ink-subtitle hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-60 dark:border-line-dark dark:text-ink-subtitle-dark dark:hover:bg-surface-dark-alt"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Lista de Licitações */}
      <div className="space-y-3">
        {filteredTenders.length === 0 ? (
          <div className="text-center py-12 bg-surface dark:bg-surface-dark rounded-lg border border-line dark:border-line-dark">
            <AlertCircle className="w-12 h-12 text-ink-placeholder dark:text-ink-placeholder-dark mx-auto mb-3" />
            <p className="text-ink-subtitle dark:text-ink-subtitle-dark">
            {opportunityView === 'ACTIVE' ? 'Nenhuma oportunidade ativa encontrada' : opportunityView === 'FAVORITED' ? 'Nenhuma licitação em participação encontrada' : opportunityView === 'ARCHIVED' ? 'Nenhuma participação arquivada encontrada' : 'Nenhuma oportunidade descartada encontrada'}
            </p>
            <p className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark mt-1">Ajuste os filtros ou selecione outra situação</p>
          </div>
        ) : (
          paginatedTenders.map((tender) => {
            const daysRemaining = getDaysRemaining(tender.encerramento);
            const isExpanded = expandedTender === tender.id;
            const categoryLabel = getOpportunityCategory(tender);
            const contractType = classifyOpportunityContract(tender);
            const contractTypeMeta = CONTRACT_TYPE_META[contractType];
            const ContractTypeIcon = contractTypeMeta.icon;
            const isExpired = daysRemaining !== null && daysRemaining < 0;
            const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 3;
            const isWarning = daysRemaining !== null && daysRemaining > 3 && daysRemaining <= 7;
            const relevanceLabel = tender.relevanceLevel === 'MUITO_ALTA'
              ? 'Muito alta'
              : tender.relevanceLevel === 'ALTA'
              ? 'Alta'
              : tender.relevanceLevel === 'MEDIA'
              ? 'Média'
              : tender.relevanceLevel === 'BAIXA'
              ? 'Baixa'
              : null;
            const relevanceClass = Number(tender.relevanceScore || 0) >= 85
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
              : Number(tender.relevanceScore || 0) >= 70
              ? 'border-blue-300 bg-blue-50 text-blue-800 dark:bg-blue-950/30 dark:text-blue-300'
              : 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300';

            return (
              <div
                key={tender.id}
                className={`bg-surface dark:bg-surface-dark border rounded-lg overflow-hidden transition-all ${
                  isUrgent
                    ? 'border-red-300 shadow-md'
                    : isWarning
                    ? 'border-yellow-300'
                    : 'border-line dark:border-line-dark hover:shadow-md'
                }`}
              >
                {/* Header (interactive div to avoid nested <button> issues) */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => setExpandedTender(isExpanded ? null : tender.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedTender(isExpanded ? null : tender.id);
                    }
                  }}
                  className={`w-full text-left p-4 ${isUrgent ? 'bg-red-50 dark:bg-red-900/30' : isWarning ? 'bg-yellow-50 dark:bg-yellow-900/30' : ''} border border-transparent hover:border-line dark:border-line-dark`}
                >
                  {/* Top row: edital/id left, location + remaining days right */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex flex-wrap items-center gap-3">
                          <label
                            className="inline-flex cursor-pointer items-center"
                            title="Selecionar oportunidade"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedTenderIds.has(tender.id)}
                              onChange={() => toggleTenderSelection(tender.id)}
                              disabled={Boolean(batchAction)}
                              aria-label={`Selecionar oportunidade ${tender.edital || tender.id}`}
                              className="h-4 w-4 rounded border-line text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
                            />
                          </label>
                          <span className="text-sm font-mono text-blue-700 dark:text-blue-300 font-semibold">{tender.edital}</span>
                          <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">ID {tender.id}</span>
                          {typeof tender.relevanceScore === 'number' && relevanceLabel && (
                            <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-semibold ${relevanceClass}`}>
                              {tender.relevanceScore}/100 · {relevanceLabel}
                            </span>
                          )}
                          {tender.decisionRecommendation && (
                            <span className="inline-flex items-center rounded border border-violet-300 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-800 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300">
                              {({ PRIORITIZE: 'Priorizar', EVALUATE: 'Avaliar', MONITOR: 'Acompanhar', DISCARD: 'Descartar' } as Record<string, string>)[tender.decisionRecommendation] || tender.decisionRecommendation} · {tender.decisionScore ?? '-'}/100
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-semibold ${
                            tender.readAt
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                              : 'border-line dark:border-line-dark bg-surface dark:bg-surface-dark text-ink-subtitle dark:text-ink-subtitle-dark'
                          }`}>
                            {tender.readAt ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            {tender.readAt ? 'Lida' : 'Não lida'}
                          </span>
                          {(opportunityView === 'FAVORITED' || opportunityView === 'ARCHIVED') && (
                            <span className="inline-flex items-center gap-1 rounded border border-brand-300 bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 dark:bg-brand-950/30 dark:text-brand-300">
                              {opportunityView === 'ARCHIVED' ? <Archive className="w-3 h-3" /> : <BookmarkCheck className="w-3 h-3" />}
                              {opportunityView === 'ARCHIVED'
                                ? tender.favoriteArchiveReason === 'NO_PARTICIPATION' ? 'Não participar' : 'Encerrada há mais de 2 dias'
                                : 'Em andamento'}
                            </span>
                          )}
                          {opportunityView === 'DISCARDED' && (
                            <span className="inline-flex items-center gap-1 rounded border border-rose-300 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
                              <Trash2 className="w-3 h-3" />{tender.discardReason === 'AUTO_RETENTION' ? 'Descarte automático' : 'Descartada'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                            <MapPin className="w-3 h-3" />
                            {tender.estado}{tender.cidade ? ` - ${tender.cidade}` : ''}
                          </div>
                          {daysRemaining !== null && (
                            <div
                              className={`w-14 h-14 shrink-0 rounded-md border grid place-items-center text-center leading-none ${
                                isExpired
                                  ? 'border-line-strong bg-surface-alt text-ink-subtitle dark:border-line-dark-strong dark:bg-surface-dark-alt dark:text-ink-subtitle-dark'
                                  : isUrgent
                                  ? 'border-red-700 bg-red-600 text-white'
                                  : isWarning
                                  ? 'border-amber-600 bg-amber-500 text-white'
                                  : 'border-brand-300 bg-brand-50 text-brand-700 dark:bg-brand-950/30 dark:text-brand-300'
                              }`}
                              title={isExpired ? `Encerrada há ${Math.abs(daysRemaining)} dia(s)` : daysRemaining === 0 ? 'Encerra hoje' : `Encerra em ${daysRemaining} dia(s)`}
                              aria-label={isExpired
                                ? `Encerrada há ${Math.abs(daysRemaining)} ${Math.abs(daysRemaining) === 1 ? 'dia' : 'dias'}`
                                : daysRemaining === 0
                                ? 'Encerra hoje'
                                : `Encerra em ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'}`}
                            >
                              <span className="text-lg font-bold tabular-nums">{isExpired ? Math.abs(daysRemaining) : daysRemaining}</span>
                              <span className="text-[9px] font-semibold uppercase">{isExpired ? 'vencida' : daysRemaining === 0 ? 'hoje' : daysRemaining === 1 ? 'dia' : 'dias'}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
                          <Tag className="w-3.5 h-3.5" />
                          Categoria: {categoryLabel}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold ${contractTypeMeta.className}`}>
                          <ContractTypeIcon className="w-3.5 h-3.5" />
                          Contratação: {contractTypeMeta.label}
                        </span>
                      </div>

                      <div className="mb-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                        <h3 className="font-bold text-ink-title dark:text-ink-title-dark">{tender.orgao}</h3>
                      </div>

                      {/* Objeto (aceita HTML vindo da API) - sanitizado */}
                      <div className="text-sm leading-relaxed text-ink-subtitle dark:text-ink-subtitle-dark whitespace-normal break-words external-description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(tender.objeto) }} />

                      {/* Meta Info */}
                      <div className="flex flex-wrap gap-3 mt-3 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Modalidade: {tender.modalidade}
                        </span>
                        {typeof tender.valorTotal === 'number' && tender.valorTotal > 0 && (
                          <span className="flex items-center gap-1 font-medium text-green-700 dark:text-green-300">
                            <DollarSign className="w-3 h-3" />
                            {formatCurrency(tender.valorTotal)}
                          </span>
                        )}
                        <span className="flex items-center gap-1 font-semibold text-ink-title dark:text-ink-title-dark">
                          <Clock className="w-3 h-3" />
                          Encerramento: {formatDate(tender.encerramento)}
                        </span>
                      </div>
                    </div>

                    {/* Right area: portal / attachment icons and expand icon (no duplicate ID shown) */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label="Analisar com o Analista IA"
                          title="Selecionar e analisar esta oportunidade com o Analista IA"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.hash = `/analyst?opportunity=${encodeURIComponent(tender.id)}&analyze=1`;
                          }}
                          className="inline-flex items-center justify-center w-9 h-9 border border-brand-300 dark:border-brand-700 rounded-md text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-950/30 hover:bg-brand-100 dark:hover:bg-brand-950/60"
                        >
                          <Bot className="w-4 h-4" />
                        </button>
                        {(opportunityView === 'FAVORITED' || opportunityView === 'ARCHIVED') && (
                          <>
                            <button
                              type="button"
                              aria-label="Participar"
                              title="Registrar que vamos participar"
                              disabled={decidingId === tender.id}
                              onClick={(e) => { e.stopPropagation(); void participateFavorite(tender); }}
                              className={`inline-flex items-center justify-center w-9 h-9 border rounded-md disabled:opacity-50 ${
                                tender.favoriteParticipationDecision === 'PARTICIPATE'
                                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                                  : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'
                              }`}
                            >
                              {decidingId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            </button>
                            <button
                              type="button"
                              aria-label={tender.favoriteTrackResult ? 'Resultado em acompanhamento' : 'Acompanhar resultado'}
                              title={tender.favoriteTrackResult ? 'Remover acompanhamento do resultado' : 'Acompanhar o resultado mesmo sem participar'}
                              disabled={trackingResultId === tender.id}
                              onClick={(e) => { e.stopPropagation(); void toggleResultTracking(tender); }}
                              className={`inline-flex items-center justify-center w-9 h-9 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                                tender.favoriteTrackResult
                                  ? 'border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300'
                                  : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt'
                              }`}
                            >
                              {trackingResultId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              type="button"
                              aria-label="Não participar"
                              title="Não participar por decisão interna, sem descartar"
                              disabled={decidingId === tender.id}
                              onClick={(e) => { e.stopPropagation(); void declineFavorite(tender); }}
                              className={`inline-flex items-center justify-center w-9 h-9 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                                tender.favoriteParticipationDecision === 'DO_NOT_PARTICIPATE'
                                  ? 'border-zinc-500 bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200'
                                  : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'
                              }`}
                            >
                              {decidingId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                            </button>
                            <button
                              type="button"
                              aria-label="Descartar"
                              title="Descartar por falta de aderência ao objeto ou aos parâmetros"
                              disabled={discardingId === tender.id}
                              onClick={(e) => { e.stopPropagation(); void handleArchive(tender); }}
                              className="inline-flex items-center justify-center w-9 h-9 border border-rose-300 dark:border-rose-700 rounded-md text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-50"
                            >
                              {discardingId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          aria-label={tender.readAt ? 'Marcar como não lida' : 'Marcar como lida'}
                          title={tender.readAt ? 'Marcar como não lida' : 'Marcar como lida'}
                          disabled={reviewingId === tender.id}
                          onClick={(e) => { e.stopPropagation(); void toggleRead(tender); }}
                          className={`inline-flex items-center justify-center w-9 h-9 border rounded-md disabled:opacity-50 ${
                            tender.readAt
                              ? 'border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300'
                              : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt'
                          }`}
                        >
                          {reviewingId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : tender.readAt ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        {tender.portalUrl && (
                          <button
                            type="button"
                            aria-label="Abrir portal"
                            onClick={(e) => { e.stopPropagation(); window.open(tender.portalUrl, '_blank', 'noopener,noreferrer'); }}
                            className="inline-flex items-center justify-center w-9 h-9 border rounded-md text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt"
                          >
                            <Globe className="w-4 h-4" />
                          </button>
                        )}
                        {(tender.attachmentUrl || tender.urlEdital) && (
                          <button
                            type="button"
                            aria-label="Abrir anexo"
                            onClick={(e) => { e.stopPropagation(); openTenderDocument(tender, tender.attachmentUrl || tender.urlEdital); }}
                            title="Abrir anexo do edital"
                            className="inline-flex items-center justify-center w-9 h-9 border rounded-md text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt"
                          >
                            <Paperclip className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <span className="p-1 text-ink-placeholder dark:text-ink-placeholder-dark">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Detalhes Expandidos */}
                {isExpanded && (
                  <div className="border-t border-line dark:border-line-dark p-4 bg-surface-alt dark:bg-surface-dark-alt space-y-4">
                    {/* Metadados principais (extenso) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm bg-surface dark:bg-surface-dark p-3 rounded">
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Globe className="w-4 h-4 text-blue-600 dark:text-blue-300" /><span>Portal de captura</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.portal ?? tender.fonte}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Hash className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Licitação</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.edital}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><FileText className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>ID da fonte</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.id}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><FileText className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Modalidade</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.modalidade}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><MapPin className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Local</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.estado}{tender.cidade ? ` - ${tender.cidade}` : ''}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Tag className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Perfil de busca</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.matchDetails?.profile ?? tender.perfilNome ?? '-'}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Tag className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Qualidade comercial</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">
                          {typeof tender.relevanceScore === 'number' ? `${tender.relevanceScore}/100 · ${relevanceLabel || '-'}` : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><DollarSign className="w-4 h-4 text-green-700 dark:text-green-300" /><span>Modelo comercial</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">
                          {tender.matchDetails?.businessModels?.join(', ') || 'Não identificado no resumo'}
                        </p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Calendar className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Data de publicação</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{formatDate(tender.incluido)}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Grid className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>UASG</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.uasg ?? '-'}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Building2 className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Fonte</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.fonte ?? '-'}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><AlertCircle className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Observações</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.observacoes ?? '-'}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Clock className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" /><span>Atualizado em</span></span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.atualizado ? formatDate(tender.atualizado) : '-'}</p>
                      </div>
                      <div>
                        <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><DollarSign className="w-4 h-4 text-green-700 dark:text-green-300" /><span>Valor Total</span></span>
                        <p className="font-medium text-green-700 dark:text-green-300">{typeof tender.valorTotal === 'number' ? formatCurrency(tender.valorTotal) : '-'}</p>
                      </div>
                      {tender.matchDetails?.reasons?.length ? (
                        <div className="md:col-span-3">
                          <span className="flex items-center gap-2 text-ink-subtitle dark:text-ink-subtitle-dark text-xs"><Search className="w-4 h-4 text-brand-600" /><span>Motivos da classificação</span></span>
                          <p className="font-medium text-ink-title dark:text-ink-title-dark">{tender.matchDetails.reasons.join(' · ')}</p>
                        </div>
                      ) : null}
                    </div>
                    {/* Datas */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-ink-subtitle dark:text-ink-subtitle-dark text-xs">Recebimento de Propostas:</span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{formatDate(tender.recebimentoPropostas)}</p>
                      </div>
                      <div>
                        <span className="text-ink-subtitle dark:text-ink-subtitle-dark text-xs">Encerramento:</span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{formatDate(tender.encerramento)}</p>
                      </div>
                      <div>
                        <span className="text-ink-subtitle dark:text-ink-subtitle-dark text-xs">Incluído em:</span>
                        <p className="font-medium text-ink-title dark:text-ink-title-dark">{formatDate(tender.incluido)}</p>
                      </div>
                    </div>

                    {/* Itens */}
                    {tender.itens && tender.itens.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-ink-text dark:text-ink-text-dark mb-2">Itens da Licitação:</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-surface-alt dark:bg-surface-dark-alt border-b border-line dark:border-line-dark">
                              <tr>
                                <th className="px-3 py-2 text-left font-semibold text-ink-subtitle dark:text-ink-subtitle-dark">Grupo</th>
                                <th className="px-3 py-2 text-left font-semibold text-ink-subtitle dark:text-ink-subtitle-dark">Itens</th>
                                <th className="px-3 py-2 text-left font-semibold text-ink-subtitle dark:text-ink-subtitle-dark">Descrição</th>
                                <th className="px-3 py-2 text-center font-semibold text-ink-subtitle dark:text-ink-subtitle-dark">Exclusivo ME/EPP</th>
                                <th className="px-3 py-2 text-center font-semibold text-ink-subtitle dark:text-ink-subtitle-dark">Quantidade</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {tender.itens.map((item, idx) => {
                                // Ensure integer quantity with no decimals
                                const qty = Number.isFinite(Number(item.quantidade)) ? Math.round(Number(item.quantidade)) : 0;
                                const isDiferenciado = Boolean(item.diferenciado);
                                return (
                                  <tr key={idx} className="hover:bg-surface-alt dark:bg-surface-dark-alt align-top">
                                    <td className="px-3 py-2 text-ink-text dark:text-ink-text-dark align-top">
                                      <div className="text-sm text-ink-text dark:text-ink-text-dark">{item.grupo && item.grupo !== '' ? item.grupo : '-'}</div>
                                    </td>
                                    <td className="px-3 py-2 text-ink-text dark:text-ink-text-dark align-top">
                                      <div className="text-sm text-ink-text dark:text-ink-text-dark">
                                        {item.item ?? '-'}
                                        {item.lote ? (<span className="block text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Lote: {item.lote}</span>) : null}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 text-ink-text dark:text-ink-text-dark align-top">
                                      <div className="external-item-desc" dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(item.descricao ?? '')) }} />
                                    </td>
                                    <td className="px-3 py-2 text-center align-top">
                                      {isDiferenciado ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200">Sim</span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-surface-alt dark:bg-surface-dark-alt text-ink-subtitle dark:text-ink-subtitle-dark">Não</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-center text-ink-text dark:text-ink-text-dark align-top">
                                      {qty}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Anexos (lista) */}
                    {tender.attachments && tender.attachments.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-ink-text dark:text-ink-text-dark mb-2">Anexos:</h4>
                        <div className="flex flex-col gap-2">
                          {tender.attachments.map((att) => (
                            <div key={att.id} className="flex items-center justify-between bg-surface dark:bg-surface-dark rounded p-2 border border-line dark:border-line-dark">
                              <div className="flex items-center gap-3">
                                <Paperclip className="w-4 h-4 text-ink-subtitle dark:text-ink-subtitle-dark" />
                                <div className="text-sm text-ink-text dark:text-ink-text-dark">{att.name}</div>
                              </div>
                              <div>
                                {att.url ? (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openTenderDocument(tender, att.url); }}
                                    className="inline-flex items-center gap-2 px-3 py-1 text-xs border rounded text-ink-subtitle dark:text-ink-subtitle-dark hover:bg-surface-alt dark:bg-surface-dark-alt"
                                  >
                                    Abrir
                                  </button>
                                ) : (
                                  <span className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark">Sem URL</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Ações */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      {opportunityView === 'ACTIVE' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void favoriteTender(tender); }}
                          disabled={Boolean(tender.favoriteTenderId) || favoritingId === tender.id}
                          className={`${theme.classes.button.primary} flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                          {favoritingId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkCheck className="w-4 h-4" />}
                          {favoritingId === tender.id ? 'Adicionando...' : 'Adicionar à participação'}
                        </button>
                      )}
                      {(opportunityView === 'FAVORITED' || opportunityView === 'ARCHIVED') && (
                        <>
                          <span className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-brand-300 bg-brand-50 text-brand-700 dark:bg-brand-950/30 dark:text-brand-300">
                            <BookmarkCheck className="w-4 h-4" />Registrada em Em andamento
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void participateFavorite(tender); }}
                            disabled={decidingId === tender.id}
                            className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border disabled:opacity-60 ${
                              tender.favoriteParticipationDecision === 'PARTICIPATE'
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                                : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'
                            }`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            {tender.favoriteParticipationDecision === 'PARTICIPATE' ? 'Participação confirmada' : 'Participar'}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void toggleResultTracking(tender); }}
                            disabled={trackingResultId === tender.id}
                            className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border disabled:opacity-60 ${
                              tender.favoriteTrackResult
                                ? 'border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300'
                                : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'
                            }`}
                          >
                            {trackingResultId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                            {tender.favoriteTrackResult ? 'Resultado em acompanhamento' : 'Acompanhar resultado'}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void declineFavorite(tender); }}
                            disabled={decidingId === tender.id}
                            className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border disabled:opacity-60 disabled:cursor-not-allowed ${
                              tender.favoriteParticipationDecision === 'DO_NOT_PARTICIPATE'
                                ? 'border-zinc-500 bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200'
                                : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'
                            }`}
                          >
                            {decidingId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                            {tender.favoriteParticipationDecision === 'DO_NOT_PARTICIPATE' ? 'Não participar: decisão interna' : 'Não participar'}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void handleArchive(tender); }}
                            disabled={discardingId === tender.id}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 disabled:opacity-60"
                          >
                            {discardingId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            Descartar
                          </button>
                        </>
                      )}
                      {opportunityView === 'DISCARDED' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void restoreTender(tender); }}
                          disabled={restoringId === tender.id}
                          className={`${theme.classes.button.primary} flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-60`}
                        >
                          {restoringId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                          Restaurar para busca ativa
                        </button>
                      )}
                      {tender.urlEdital ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); openTenderDocument(tender, tender.urlEdital); }}
                          className={`${theme.classes.button.outline} flex items-center gap-2 px-4 py-2 text-sm`}
                        >
                          <Eye className="w-4 h-4" />
                          Ver Edital
                        </button>
                      ) : (
                        <button
                          className={`${theme.classes.button.outline} flex items-center gap-2 px-4 py-2 text-sm opacity-50 cursor-not-allowed`}
                          disabled
                        >
                          <Eye className="w-4 h-4" />
                          Edital Indisponível
                        </button>
                      )}
                      {opportunityView === 'ACTIVE' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleArchive(tender); }}
                          disabled={discardingId === tender.id}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-rose-600 rounded hover:bg-rose-50"
                        >
                          {discardingId === tender.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          Descartar
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); void toggleRead(tender); }}
                        disabled={reviewingId === tender.id}
                        className={`${theme.classes.button.outline} flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-60`}
                      >
                        {tender.readAt ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        {tender.readAt ? 'Marcar como não lida' : 'Marcar como lida'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {filteredTenders.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-4 py-3">
          <span className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
            Mostrando {pageStart + 1} a {Math.min(pageStart + OPPORTUNITIES_PER_PAGE, filteredTenders.length)} de {filteredTenders.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
              className="btn-secondary disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" /> Anterior
            </button>
            <span className="min-w-[110px] text-center text-sm font-medium text-ink-title dark:text-ink-title-dark">
              Página {currentPage} de {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage >= totalPages}
              className="btn-secondary disabled:opacity-40"
            >
              Próximo <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalTenderList;
